<template>
  <BaseModal
    :model-value="modelValue"
    :title="modalTitle"
    :title-logo="userIcon"
    width="640px"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="$emit('update:modelValue', false)"
  >
    <!-- Content -->
    <div>
      <DropdownQuestion
        v-model="selectedPatientId"
        :options="formattedPatientOptions"
        question="Select Patients"
        placeholder="Please select"
        select-width="100%"
        popper-class="switch-patient-dropdown-popper"
        :filterable="true"
        :remote="true"
        :remote-method="handleRemoteSearch"
        :loading="isLoadingPatients"
        :has-more="hasMore"
        class="switch-patient-dropdown"
        @change="handlePatientChange"
        @load-more="fetchNextPage"
      >
        <template #option="{ item }">
          <div class="flex items-center gap-2">
            <img
              src="@/assets/my-calendar/task/person.svg"
              alt="person"
              class="w-6 h-6"
            />
            <span>{{ item.label }}</span>
          </div>
        </template>
      </DropdownQuestion>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <NewButton
          type="normal"
          color-type="red-stroke"
          @click="$emit('update:modelValue', false)"
        >
          Cancel
        </NewButton>
        <NewButton
          type="normal"
          color-type="dark-blue"
          :disabled="!selectedPatientId"
          @click="handleConfirm"
        >
          Confirm
        </NewButton>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { createEncounterNoteApi } from '@/api/encounterNotes';
import { searchPatientApi } from '@/api/va-order-page/orderTest';
import BaseModal from './BaseModal.vue';
import DropdownQuestion from '@/views/setting/my-practice/components/DropdownQuestion.vue';
import NewButton from '@/NewLibrary/Button.vue';
import userIcon from '@/assets/encounterNotes/person.svg';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  defaultPatientId: {
    type: [String, Number],
    default: null,
  },
  defaultPatientName: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
});

const modalTitle = computed(() => {
  if (props.title) return props.title;
  return props.defaultPatientId ? 'Create a new note' : 'Switch to another Patient';
});

const emit = defineEmits(['update:modelValue', 'confirm']);

const router = useRouter();
const store = useStore();
const selectedPatientId = ref(null);
const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
let searchDebounceTimer = null;

// Read state from the dedicated encounter-note-patient store
const isLoadingPatients = computed(() => store.state.encounterNotePatient.loading || isSearching.value);
const hasMore = computed(() => {
  if (searchQuery.value) return false;
  return store.getters['encounterNotePatient/hasMore'];
});
const allPatients = computed(() => {
  if (searchQuery.value) return searchResults.value;
  return store.getters['encounterNotePatient/patientList'];
});

const patientLabel = (patient) => {
  const firstName = patient.patient_first_name || '';
  const lastName = patient.patient_last_name || '';
  const name = `${firstName} ${lastName}`.trim();
  return name || `Patient ${patient.patient_id}`;
};

const defaultPatientOption = computed(() => {
  if (!props.defaultPatientId) return null;
  return {
    value: String(props.defaultPatientId),
    label: props.defaultPatientName || `Patient ${props.defaultPatientId}`,
  };
});

const formattedPatientOptions = computed(() => {
  const defaultId = props.defaultPatientId ? String(props.defaultPatientId) : null;

  // Filter out default patient from API results to avoid duplicates
  const options = allPatients.value
    .filter((patient) => !defaultId || String(patient.patient_id) !== defaultId)
    .map((patient) => ({
      value: String(patient.patient_id),
      label: patientLabel(patient),
      patient: patient,
    }));

  // Prepend default patient as first option when not searching
  if (defaultPatientOption.value && !searchQuery.value) {
    return [defaultPatientOption.value, ...options];
  }

  return options;
});

const fetchNextPage = () => {
  store.dispatch('encounterNotePatient/fetchNextPage');
};

const handleRemoteSearch = (query) => {
  clearTimeout(searchDebounceTimer);
  searchQuery.value = query;

  if (!query) {
    searchResults.value = [];
    isSearching.value = false;
    return;
  }

  if (query.length < 2) return;

  isSearching.value = true;
  searchDebounceTimer = setTimeout(async () => {
    try {
      const { data } = await searchPatientApi({ inputPatient: query });
      searchResults.value = data || [];
    } catch (error) {
      console.error('Failed to search patients:', error);
      searchResults.value = [];
    } finally {
      isSearching.value = false;
    }
  }, 300);
};

const handlePatientChange = (value) => {
  selectedPatientId.value = value;
};

const isCreatingNote = ref(false);

const handleConfirm = async () => {
  if (!selectedPatientId.value || isCreatingNote.value) return;

  // Check API results first, then fall back to default patient from props
  let selectedPatient = allPatients.value.find(
    (p) => String(p.patient_id) === String(selectedPatientId.value)
  );
  if (!selectedPatient && defaultPatientOption.value && String(selectedPatientId.value) === defaultPatientOption.value.value) {
    selectedPatient = { patient_id: props.defaultPatientId, patient_first_name: props.defaultPatientName };
  }

  if (selectedPatient) {
    isCreatingNote.value = true;

    try {
      const userInfo = store.state.login?.userInfo;
      const username = userInfo?.customer_name || 'Unknown';

      const response = await createEncounterNoteApi({
        patient_id: String(selectedPatientId.value),
        title: `Created by ${username}`,
        appointment_type: "General"
      });

      if (response && response.id) {
        emit('confirm', selectedPatient);
        emit('update:modelValue', false);
        router.push(`/encounter-notes/${selectedPatientId.value}/${response.id}`);
      }
    } catch (error) {
      console.error('Failed to create encounter note:', error);
    } finally {
      isCreatingNote.value = false;
    }
  }
};

// Reset selection when modal closes; fetch first page when modal opens
watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) {
      selectedPatientId.value = null;
      searchQuery.value = '';
      searchResults.value = [];
      clearTimeout(searchDebounceTimer);
    } else {
      if (allPatients.value.length === 0) {
        fetchNextPage();
      }
      if (props.defaultPatientId) {
        selectedPatientId.value = String(props.defaultPatientId);
      }
    }
  }
);
</script>

<style scoped>
:deep(.switch-patient-dropdown .el-select__wrapper) {
  box-shadow: 0 1px 3px rgba(74, 74, 74, 0.12);
  min-height: 42px;
  height: 42px;
}

:deep(.switch-patient-dropdown .el-select__wrapper .el-select__input) {
  height: 100%;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #999;
}

:deep(.switch-patient-dropdown .el-select__wrapper .el-select__placeholder) {
  color: #999;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
}
</style>
